import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  IndianRupee,
  Car,
  Wrench,
  Shield,
  MapPin,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Globe,
  X,
  ChevronRight,
  Bell,
  ChevronDown,
  Building2,
  Smartphone,
  Calendar,
} from 'lucide-react';
import { AdminService } from '../../services/adminService';
import { EnterpriseAdminService } from '../../services/enterpriseAdminService';
import { marketingAdmin } from '../../services/marketingAdminService';
import { notifyAppEnquiryWhatsApp, sendMissingPartnerJobNotifyWhatsApp } from '../../services/appEnquiryWhatsAppService';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';
import { drivingDistancesById } from '../../utils/drivingDistance';
import ComplaintJobChat from '../../components/service_advisor/ComplaintJobChat';

const PAGE_SIZE = 20;
const SEEN_KEY = 'rs_app_enquiry_seen_v1';

const TABS = [
  { id: 'car_loan', label: 'Car Loan', icon: IndianRupee },
  { id: 'car_service', label: 'Car Service', icon: Wrench },
  { id: 'rsa', label: 'RSA', icon: Shield },
  { id: 'website', label: 'Website', icon: Globe },
];

const RSA_STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'scheduled', label: 'Scheduled', icon: Calendar },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

const RSA_SOURCE_FILTERS = [
  { id: 'all', label: 'All jobs' },
  { id: 'b2b', label: 'B2B', icon: Building2 },
  { id: 'website', label: 'Website', icon: Globe },
];

// Website RSA complaints are real jobs (service_requests.source = website_rsa).
// The marketing site also logs them as website_leads (source = rsa_web) for
// analytics — keep those off the Website tab so the job shows once, under RSA.
const WEBSITE_LEADS_EXCLUDE_RSA = 'source.is.null,source.neq.rsa_web';
const isWebsiteRsa = (row) => String(row?.source || '').trim() === 'website_rsa';

const RSA_FILTER_SELECT =
  'id, customer_id, customer_name, contact_number, service_type, vehicle_number, vehicle_brand, vehicle_model, pickup_address, pickup_latitude, pickup_longitude, location_link, request_status, complaint_status, source, pricing_mode, rate_status, customer_quoted_amount, admin_quoted_amount, agreed_amount, needs_admin_rate, open_for_partner_accept, assigned_mechanic_id, assigned_mechanic_name, membership_subscription_id, problem, requested_at, complaint_registered_at, updated_at, delete_status, enterprise_id, enterprise_ref, enterprise_rate_pick_mode, mechanic_completed_datetime, scheduled_at, booking_mode';

const statusColors = {
  submitted: 'bg-blue-100 text-blue-700',
  pending: 'bg-yellow-100 text-yellow-700',
  pending_activation: 'bg-amber-100 text-amber-800',
  active: 'bg-green-100 text-green-700',
  paid: 'bg-emerald-100 text-emerald-800',
  confirmed: 'bg-green-100 text-green-700',
  requested: 'bg-orange-100 text-orange-700',
  registered: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  expired: 'bg-gray-100 text-gray-700',
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-800',
  converted: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
  negotiating: 'bg-violet-100 text-violet-700',
  admin_quoted: 'bg-teal-100 text-teal-800',
  awaiting_admin_quote: 'bg-amber-100 text-amber-800',
  awaiting_mechanic_quote: 'bg-amber-100 text-amber-800',
  pending_review: 'bg-amber-100 text-amber-800',
  rejected: 'bg-red-100 text-red-700',
  draft: 'bg-sky-100 text-sky-800',
  rsa_job: 'bg-emerald-100 text-emerald-800',
  session: 'bg-sky-100 text-sky-800',
  collecting: 'bg-amber-100 text-amber-800',
};

const WEBSITE_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
];

const fmtDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtScheduledWhen = (value) => {
  const ms = new Date(value || 0).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  return new Date(ms).toLocaleString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const parseGarageNotes = (notes) => {
  if (!notes) return {};
  try {
    return typeof notes === 'string' ? JSON.parse(notes) : notes;
  } catch {
    return {};
  }
};

const hasGps = (row) => {
  const lat = Number(row.pickup_latitude);
  const lng = Number(row.pickup_longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};

const fmtGps = (row) => {
  if (!hasGps(row)) return null;
  const lat = Number(row.pickup_latitude);
  const lng = Number(row.pickup_longitude);
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const mapsUrl = (row) => {
  if (row.location_link) return row.location_link;
  if (!hasGps(row)) return null;
  const lat = Number(row.pickup_latitude);
  const lng = Number(row.pickup_longitude);
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

const fmtMembershipCard = (card) => {
  if (!card) return null;
  const digits = String(card).replace(/\D/g, '');
  if (digits.length !== 16) return card;
  return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12, 16)}`;
};

const fmtVehicleTitle = (row) => {
  const parts = [row.vehicle_brand, row.vehicle_model].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
};

const SOURCE_LABELS = {
  customer_rsa_app: 'Customer App (RSA)',
  customer_rsa_bargain: 'Customer App (Bargain)',
  customer_rsa_on_demand: 'Customer App (On-demand)',
  whatsapp_ai: 'WhatsApp AI',
  whatsapp_rsa: 'WhatsApp RSA',
  website_rsa: 'Website RSA',
  enterprise_manual: 'Enterprise portal',
  enterprise_api: 'Enterprise API',
  website: 'Website',
};

const isEnterpriseRsa = (row) => {
  const src = String(row?.source || '').trim();
  return src === 'enterprise_manual' || src === 'enterprise_api' || !!row?.enterprise_id;
};

const nestedEnterpriseName = (job) => {
  const nested = job?.enterprises;
  if (Array.isArray(nested)) {
    return String(nested[0]?.name || nested[0]?.legal_name || '').trim();
  }
  return String(nested?.name || nested?.legal_name || '').trim();
};

const enterpriseDisplayName = (row) =>
  String(row?.enterprise_name || nestedEnterpriseName(row) || row?.enterprise_ref || '').trim();

const fmtEnquirySource = (row) => {
  const src = String(row?.source || '').trim();
  const fromWa =
    !!row?.from_whatsapp ||
    src === 'whatsapp_ai' ||
    src === 'whatsapp_rsa' ||
    src.includes('whatsapp');
  if (fromWa) return 'WhatsApp AI';
  if (isEnterpriseRsa(row)) {
    const label = SOURCE_LABELS[src] || 'Enterprise B2B';
    const company = enterpriseDisplayName(row);
    return company ? `${label} · ${company}` : label;
  }
  if (!src) return '—';
  return SOURCE_LABELS[src] || src.replace(/_/g, ' ');
};

const rowCreatedAt = (tab, row) => {
  if (tab === 'rsa') {
    return row.complaint_registered_at || row.requested_at || row.created_at;
  }
  return row.created_at;
};

const loadSeenMap = () => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') || {};
  } catch {
    return {};
  }
};

const saveSeenMap = (map) => {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
};

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = statusColors[key] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-md ${cls}`}>
      {value || '—'}
    </span>
  );
};

const isRsaCompleted = (row) => {
  const req = String(row.request_status || '').toLowerCase();
  const comp = String(row.complaint_status || '').toLowerCase();
  return (
    !!row.mechanic_completed_datetime ||
    ['completed', 'complete'].includes(req) ||
    ['completed', 'complete'].includes(comp)
  );
};

const isRsaCancelled = (row) => {
  const req = String(row.request_status || '').toLowerCase();
  const comp = String(row.complaint_status || '').toLowerCase();
  return (
    ['cancelled', 'canceled', 'closed'].includes(req) ||
    ['cancelled', 'canceled', 'closed'].includes(comp)
  );
};

const isRsaClosed = (row) => isRsaCompleted(row) || isRsaCancelled(row);

const isRsaScheduledJob = (row) =>
  String(row?.booking_mode || '').toLowerCase() === 'scheduled' || !!row?.scheduled_at;

const rsaScheduledMs = (row) => {
  const ms = new Date(row?.scheduled_at || 0).getTime();
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
};

const isRsaScheduledAccepted = (row) =>
  !!(row?.assigned_mechanic_id || String(row?.assigned_mechanic_name || '').trim());

const isRsaScheduledExpiredUnaccepted = (row) => {
  if (!isRsaScheduledJob(row) || isRsaScheduledAccepted(row)) return false;
  const ms = rsaScheduledMs(row);
  return ms > 0 && ms < Date.now();
};

const isRsaScheduledPending = (row) => {
  if (isRsaClosed(row) || !isRsaScheduledJob(row) || isRsaScheduledAccepted(row)) return false;
  const ms = rsaScheduledMs(row);
  return !ms || ms >= Date.now();
};

const rsaStatusBucket = (row) => {
  if (isRsaCompleted(row)) return 'completed';
  if (isRsaCancelled(row)) return 'cancelled';
  if (isRsaScheduledExpiredUnaccepted(row)) return 'expired';
  if (isRsaScheduledPending(row)) return 'scheduled';
  return 'open';
};

const mapRsaFilterRow = (job) => {
  const user = Array.isArray(job.users) ? job.users[0] : job.users;
  const enterprise = Array.isArray(job.enterprises) ? job.enterprises[0] : job.enterprises;
  const src = String(job.source || '');
  return {
    id: job.id,
    customer_id: job.customer_id,
    customer_name: job.customer_name || user?.name || 'Customer',
    customer_phone: job.contact_number || user?.phone,
    customer_email: user?.email || job.customer_email,
    service_type: job.service_type,
    pickup_address: job.pickup_address,
    pickup_latitude: job.pickup_latitude,
    pickup_longitude: job.pickup_longitude,
    location_link: job.location_link,
    vehicle_registration: job.vehicle_registration || job.vehicle_number,
    vehicle_brand: job.vehicle_brand,
    vehicle_model: job.vehicle_model,
    request_status: job.request_status,
    complaint_status: job.complaint_status,
    source: job.source,
    pricing_mode: job.pricing_mode,
    rate_status: job.rate_status,
    customer_quoted_amount: job.customer_quoted_amount,
    admin_quoted_amount: job.admin_quoted_amount,
    agreed_amount: job.agreed_amount,
    needs_admin_rate: job.needs_admin_rate,
    open_for_partner_accept: job.open_for_partner_accept,
    assigned_mechanic_id: job.assigned_mechanic_id,
    assigned_mechanic_name: job.assigned_mechanic_name,
    membership_subscription_id: job.membership_subscription_id,
    problem: job.problem,
    enterprise_id: job.enterprise_id,
    enterprise_name:
      job.enterprise_name || enterprise?.name || nestedEnterpriseName(job) || job.enterprise_ref,
    enterprise_ref: job.enterprise_ref,
    enterprise_rate_pick_mode: job.enterprise_rate_pick_mode,
    mechanic_completed_datetime: job.mechanic_completed_datetime,
    scheduled_at: job.scheduled_at,
    booking_mode: job.booking_mode,
    from_whatsapp: src === 'whatsapp_ai' || src === 'whatsapp_rsa' || !!job.from_whatsapp,
    requested_at: job.requested_at,
    complaint_registered_at: job.complaint_registered_at,
    created_at: job.complaint_registered_at || job.requested_at || job.updated_at,
  };
};

const applyRsaStatusFilter = (query, status) => {
  if (status === 'completed') {
    return query.or(
      [
        'request_status.ilike.completed',
        'request_status.ilike.complete',
        'complaint_status.ilike.completed',
        'complaint_status.ilike.complete',
        'mechanic_completed_datetime.not.is.null',
      ].join(','),
    );
  }
  if (status === 'cancelled') {
    return query.or(
      [
        'request_status.ilike.cancelled',
        'request_status.ilike.canceled',
        'request_status.ilike.closed',
        'complaint_status.ilike.cancelled',
        'complaint_status.ilike.canceled',
        'complaint_status.ilike.closed',
      ].join(','),
    );
  }
  if (status === 'open') {
    return query
      .is('mechanic_completed_datetime', null)
      .or('request_status.is.null,request_status.not.in.(completed,complete,cancelled,canceled,closed)')
      .or('complaint_status.is.null,complaint_status.not.in.(completed,complete,cancelled,canceled,closed)')
      .or('scheduled_at.is.null,assigned_mechanic_id.not.is.null');
  }
  if (status === 'scheduled') {
    const nowIso = new Date().toISOString();
    return query
      .is('mechanic_completed_datetime', null)
      .is('assigned_mechanic_id', null)
      .or('booking_mode.eq.scheduled,scheduled_at.not.is.null')
      .gte('scheduled_at', nowIso)
      .or('request_status.is.null,request_status.not.in.(completed,complete,cancelled,canceled,closed)')
      .or('complaint_status.is.null,complaint_status.not.in.(completed,complete,cancelled,canceled,closed)');
  }
  return query;
};

let enterpriseNameCache = { at: 0, byId: new Map() };

async function loadEnterpriseNameMap() {
  if (Date.now() - enterpriseNameCache.at < 60_000 && enterpriseNameCache.byId.size) {
    return enterpriseNameCache.byId;
  }
  try {
    const items = await EnterpriseAdminService.list();
    const byId = new Map(
      (items || [])
        .filter((e) => e?.id)
        .map((e) => [String(e.id), String(e.name || e.legal_name || '').trim()]),
    );
    enterpriseNameCache = { at: Date.now(), byId };
    return byId;
  } catch (err) {
    console.warn('loadEnterpriseNameMap:', err?.message || err);
    return enterpriseNameCache.byId;
  }
}

async function attachEnterpriseNames(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const needsName = (r) => isEnterpriseRsa(r) && !enterpriseDisplayName(r);
  if (!list.some(needsName)) {
    return list.map((r) => ({
      ...r,
      enterprise_name: r.enterprise_name || nestedEnterpriseName(r) || r.enterprise_ref || null,
    }));
  }

  const db = serviceRoleClient || supabase;
  const nameless = list.filter(needsName);
  const missingIds = nameless.filter((r) => !r.enterprise_id).map((r) => r.id);
  let idPatch = new Map();
  if (missingIds.length) {
    const { data } = await db
      .from('service_requests')
      .select('id, enterprise_id, enterprise_ref')
      .in('id', missingIds);
    idPatch = new Map((data || []).map((row) => [row.id, row]));
  }

  const byEntId = await loadEnterpriseNameMap();
  const byReqId = new Map();
  const stillMissing = nameless.filter((r) => {
    const patched = idPatch.get(r.id);
    const entId = r.enterprise_id || patched?.enterprise_id;
    return !byEntId.get(String(entId || ''));
  });
  if (stillMissing.length) {
    try {
      const res = await AdminService.getAppEnquiries('rsa', { limit: 80, offset: 0 });
      (res.rows || []).forEach((row) => {
        const name = String(row.enterprise_name || row.enterprise_ref || '').trim();
        if (!name) return;
        if (row.id) byReqId.set(row.id, name);
        if (row.enterprise_id) byEntId.set(String(row.enterprise_id), name);
      });
    } catch (err) {
      console.warn('attachEnterpriseNames rpc:', err?.message || err);
    }
  }

  return list.map((r) => {
    const patched = idPatch.get(r.id);
    const enterpriseId = r.enterprise_id || patched?.enterprise_id || null;
    const enterpriseRef = r.enterprise_ref || patched?.enterprise_ref || null;
    return {
      ...r,
      enterprise_id: enterpriseId,
      enterprise_ref: enterpriseRef,
      enterprise_name:
        r.enterprise_name ||
        nestedEnterpriseName(r) ||
        (enterpriseId ? byEntId.get(String(enterpriseId)) : null) ||
        byReqId.get(r.id) ||
        enterpriseRef ||
        null,
    };
  });
}

async function fetchRsaFilteredPage(search, offset, status, source = 'all') {
  const db = serviceRoleClient || supabase;
  const safeOffset = Math.max(0, Number(offset) || 0);
  const term = search ? search.replace(/[%_,]/g, '').trim() : '';
  const b2bOnly = source === 'b2b';
  const websiteOnly = source === 'website';

  const run = async (selectCols) => {
    let query = db
      .from('service_requests')
      .select(selectCols, { count: 'exact' })
      .or('delete_status.is.null,delete_status.eq.false');
    if (b2bOnly) {
      query = query.or(
        'source.in.(enterprise_manual,enterprise_api),enterprise_id.not.is.null',
      );
    } else if (websiteOnly) {
      query = query.eq('source', 'website_rsa');
    } else {
      query = query.or(
        'source.in.(customer_rsa_app,customer_rsa_bargain,customer_rsa_on_demand,whatsapp_ai,whatsapp_rsa,website_rsa,enterprise_manual,enterprise_api),enterprise_id.not.is.null',
      );
    }
    query = applyRsaStatusFilter(query, status);
    if (term) {
      query = query.or(
        [
          `customer_name.ilike.%${term}%`,
          `contact_number.ilike.%${term}%`,
          `vehicle_number.ilike.%${term}%`,
          `service_type.ilike.%${term}%`,
          `pickup_address.ilike.%${term}%`,
          `enterprise_ref.ilike.%${term}%`,
        ].join(','),
      );
    }
    return query
      .order(status === 'scheduled' ? 'scheduled_at' : 'complaint_registered_at', {
        ascending: status === 'scheduled',
        nullsFirst: false,
      })
      .range(safeOffset, safeOffset + PAGE_SIZE - 1);
  };

  let result = await run(
    `${RSA_FILTER_SELECT}, users:customer_id ( name, phone, email ), enterprises ( name )`,
  );
  if (result.error) {
    result = await run(`${RSA_FILTER_SELECT}, enterprises ( name )`);
  }
  if (result.error) {
    result = await run(RSA_FILTER_SELECT);
  }
  if (result.error) {
    const res = await AdminService.getAppEnquiries('rsa', {
      search,
      limit: PAGE_SIZE,
      offset: safeOffset,
    });
    if (!res.success && res.error) throw new Error(res.error);
    const rows = await attachEnterpriseNames(
      (res.rows || [])
        .map(mapRsaFilterRow)
        .filter((row) => {
          if (b2bOnly && !isEnterpriseRsa(row)) return false;
          if (websiteOnly && !isWebsiteRsa(row)) return false;
          if (status && status !== 'all' && rsaStatusBucket(row) !== status) return false;
          return true;
        }),
    );
    return {
      rows,
      total: Number(res.total) || rows.length,
      offset: safeOffset,
      fetched: (res.rows || []).length,
    };
  }

  const raw = result.data || [];
  const rows = await attachEnterpriseNames(
    raw
      .map(mapRsaFilterRow)
      .filter((row) => (b2bOnly ? isEnterpriseRsa(row) : websiteOnly ? isWebsiteRsa(row) : true)),
  );
  return {
    rows,
    total: Number(result.count) || 0,
    offset: safeOffset,
    fetched: raw.length,
  };
}

/** Active/linked RSA membership — no admin rate quote needed */
const isMembershipRsa = (row) => {
  if (!row) return false;
  if (row.membership_subscription_id) return true;
  if (String(row.pricing_mode || '').toLowerCase() === 'membership_covered') return true;
  if (row.membership_plan_name || row.membership_card_number) return true;
  return false;
};

const isSimpleEnquiryClosed = (row) => {
  const status = String(row.status || '').toLowerCase();
  return ['completed', 'cancelled', 'rejected', 'closed', 'converted'].includes(status);
};

const EnquiryActionButtons = ({ closed, updating, onComplete, onCancel }) => {
  if (closed) {
    return <span className="text-xs text-gray-400">Closed</span>;
  }
  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={onComplete}
        disabled={!!updating}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
      >
        <CheckCircle2 size={14} />
        {updating === 'complete' ? '...' : 'Complete'}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={!!updating}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50"
      >
        <XCircle size={14} />
        {updating === 'cancel' ? '...' : 'Cancel'}
      </button>
    </div>
  );
};

const RsaLocationBlock = ({ row }) => {
  const gps = fmtGps(row);
  const url = mapsUrl(row);
  if (!gps && !row.pickup_address) {
    return <span className="text-sm text-gray-400">—</span>;
  }
  return (
    <div className="space-y-1">
      {gps ? (
        <>
          <div className="inline-flex items-start gap-1.5 text-sm font-mono text-gray-900">
            <MapPin size={14} className="text-red-500 mt-0.5 shrink-0" />
            <span>{gps}</span>
          </div>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
            >
              Open in Google Maps
              <ExternalLink size={12} />
            </a>
          ) : null}
        </>
      ) : null}
      {row.pickup_address ? (
        <p className="text-xs text-gray-500 line-clamp-2" title={row.pickup_address}>
          {row.pickup_address}
        </p>
      ) : null}
    </div>
  );
};

const dbClient = () => serviceRoleClient || supabase;

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const a = Number(lat1);
  const b = Number(lng1);
  const c = Number(lat2);
  const d = Number(lng2);
  if (![a, b, c, d].every(Number.isFinite)) return null;
  const toRad = (n) => (n * Math.PI) / 180;
  const dLat = toRad(c - a);
  const dLng = toRad(d - b);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a)) * Math.cos(toRad(c)) * Math.sin(dLng / 2) ** 2;
  return Math.round(6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)) * 10) / 10;
};

const partnerMatchesJobSpec = (mech, serviceType) => {
  const needed = String(serviceType || '').trim().toLowerCase();
  if (!needed) return true;
  const tags = [mech.service_tag, mech.service_tag2, mech.service_tag3]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!tags.trim()) return false;
  const hit = (words) => words.some((w) => tags.includes(w));
  if (needed.includes('fuel') || needed.includes('petrol') || needed.includes('diesel')) {
    return hit(['fuel', 'petrol', 'diesel']);
  }
  if (needed.includes('jump') || needed.includes('battery')) return hit(['jump', 'battery']);
  if (needed.includes('punct') || needed.includes('flat') || needed.includes('tyre') || needed.includes('tire')) {
    return hit(['punct', 'flat', 'tyre', 'tire']);
  }
  if (needed.includes('tow')) return hit(['tow']);
  if (needed.includes('lock') || needed.includes('key')) return hit(['lock', 'key']);
  if (needed.includes('minor') || needed.includes('repair') || needed.includes('break')) {
    return hit(['minor', 'repair', 'break']);
  }
  return tags.includes(needed);
};

const phoneVariants = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  const phone10 = digits.length >= 10 ? digits.slice(-10) : digits;
  return [...new Set([phone, phone10, phone10 ? `91${phone10}` : null, digits || null].filter(Boolean))];
};

const flavorLabel = (flavor) => {
  const f = String(flavor || '').toLowerCase();
  if (f === 'mechanic' || f === 'partner') return 'Partner app';
  if (f === 'customer' || f === 'carcare') return 'Customer app';
  if (f === 'staff') return 'Staff app';
  return flavor || 'App';
};

const platformLabel = (platform) => {
  const p = String(platform || '').toLowerCase();
  if (p === 'android') return 'Android';
  if (p === 'ios') return 'iPhone';
  if (p === 'web') return 'Web';
  return platform || 'Device';
};

const deviceModelLabel = (d) => {
  const model = String(d?.device_model || '').trim();
  const brand = String(d?.device_brand || '').trim();
  if (model && brand && !model.toLowerCase().includes(brand.toLowerCase())) {
    return `${brand} ${model}`;
  }
  return model || brand || null;
};

const shortDeviceId = (id) => {
  const s = String(id || '').trim();
  if (!s) return null;
  return s.length <= 10 ? s : `…${s.slice(-8)}`;
};

const tokenMatchKey = (userId, deviceId) => `${userId || ''}::${deviceId || ''}`;

const attachPushDevices = async (requestId, mechanics) => {
  const list = Array.isArray(mechanics) ? mechanics : [];
  if (!list.length) return list;
  const db = dbClient();
  const ids = list.map((m) => m.mechanic_id).filter(Boolean);

  const [sendsRes, waRes, mechRes, crewRes] = await Promise.all([
    db
      .from('partner_job_push_sends')
      .select('mechanic_id, user_id, platform, device_id, app_flavor, device_brand, device_model, ok, error, sent_at, delivered_at')
      .eq('request_id', requestId)
      .then((r) =>
        r.error
          ? db
              .from('partner_job_push_sends')
              .select('mechanic_id, user_id, platform, device_id, app_flavor, ok, error, sent_at')
              .eq('request_id', requestId)
          : r,
      )
      .catch(() => ({ data: [], error: true })),
    db
      .from('partner_job_whatsapp_log')
      .select('mechanic_id, phone, status')
      .eq('request_id', requestId)
      .then((r) => r)
      .catch(() => ({ data: [], error: true })),
    db.from('mechanics').select('id, owner_user_id').in('id', ids),
    db
      .from('partner_crew_members')
      .select('partner_id, user_id, display_name, phone, status')
      .in('partner_id', ids)
      .eq('status', 'active')
      .then((r) => r)
      .catch(() => ({ data: [] })),
  ]);

  const sends = Array.isArray(sendsRes?.data) ? sendsRes.data : [];
  const waRows = (Array.isArray(waRes?.data) ? waRes.data : []).filter((w) => {
    const st = String(w.status || 'sent').toLowerCase();
    return st === 'sent' || st === '';
  });
  const ownerByMech = new Map((mechRes.data || []).map((m) => [String(m.id), m.owner_user_id]));
  const crewByMech = new Map();
  for (const c of crewRes.data || []) {
    const key = String(c.partner_id);
    if (!crewByMech.has(key)) crewByMech.set(key, []);
    crewByMech.get(key).push(c);
  }

  const userIds = [
    ...new Set(
      [
        ...ids.map((id) => ownerByMech.get(String(id))),
        ...(crewRes.data || []).map((c) => c.user_id),
        ...sends.map((s) => s.user_id),
      ].filter(Boolean),
    ),
  ];

  const [tokenRes, userRes] = userIds.length
    ? await Promise.all([
        db
          .from('user_device_push_tokens')
          .select('user_id, platform, device_id, app_flavor, device_brand, device_model, last_seen_at')
          .in('user_id', userIds)
          .order('last_seen_at', { ascending: false })
          .then((r) =>
            r.error
              ? db
                  .from('user_device_push_tokens')
                  .select('user_id, platform, device_id, app_flavor, last_seen_at')
                  .in('user_id', userIds)
                  .order('last_seen_at', { ascending: false })
              : r,
          ),
        db.from('users').select('id, name, phone').in('id', userIds),
      ])
    : [{ data: [] }, { data: [] }];

  const userById = new Map((userRes.data || []).map((u) => [String(u.id), u]));
  const tokensByUser = new Map();
  const tokenByDevice = new Map();
  for (const t of tokenRes.data || []) {
    const key = String(t.user_id);
    if (!tokensByUser.has(key)) tokensByUser.set(key, []);
    tokensByUser.get(key).push(t);
    tokenByDevice.set(tokenMatchKey(t.user_id, t.device_id), t);
  }

  const waPhoneByMech = new Map();
  const waByPhone = new Set();
  for (const w of waRows) {
    const mid = String(w.mechanic_id || '');
    const phone = String(w.phone || '').replace(/\D/g, '').slice(-10);
    if (phone) waByPhone.add(phone);
    if (mid) waPhoneByMech.set(mid, phone || waPhoneByMech.get(mid) || '');
  }

  return list.map((m) => {
    const mid = String(m.mechanic_id);
    const ownerId = ownerByMech.get(mid);
    const crew = crewByMech.get(mid) || [];
    const mechSends = sends.filter((s) => String(s.mechanic_id) === mid);
    const fcmLogged = mechSends.length > 0;
    const fcmDelivered = mechSends.some((s) => s.delivered_at);
    const fcmOk = mechSends.some((s) => s.ok);
    const fcmFailed = fcmLogged && !fcmOk;

    const deviceFromSends = mechSends.map((s) => {
      const u = userById.get(String(s.user_id || '')) || {};
      const isCrew = crew.some((c) => String(c.user_id) === String(s.user_id));
      const tok = tokenByDevice.get(tokenMatchKey(s.user_id, s.device_id)) || {};
      return {
        who: isCrew
          ? crew.find((c) => String(c.user_id) === String(s.user_id))?.display_name || 'Crew'
          : 'Owner',
        name: u.name || null,
        platform: s.platform,
        device_id: s.device_id,
        app_flavor: s.app_flavor,
        device_brand: s.device_brand || tok.device_brand || null,
        device_model: s.device_model || tok.device_model || null,
        ok: s.ok,
        delivered: !!s.delivered_at,
        error: s.error,
        at: s.sent_at,
        source: 'fcm_log',
      };
    });

    const liveUserIds = [ownerId, ...crew.map((c) => c.user_id)].filter(Boolean);
    const liveDevices = [];
    if (!deviceFromSends.length) {
      for (const uid of liveUserIds) {
        const isCrew = crew.some((c) => String(c.user_id) === String(uid));
        const who = isCrew
          ? crew.find((c) => String(c.user_id) === String(uid))?.display_name || 'Crew'
          : 'Owner';
        const u = userById.get(String(uid)) || {};
        const tokens = tokensByUser.get(String(uid)) || [];
        if (!tokens.length) {
          liveDevices.push({
            who,
            name: u.name || null,
            platform: null,
            device_id: null,
            app_flavor: null,
            last_seen_at: null,
            source: 'no_token',
          });
          continue;
        }
        for (const t of tokens.slice(0, 3)) {
          liveDevices.push({
            who,
            name: u.name || null,
            platform: t.platform,
            device_id: t.device_id,
            app_flavor: t.app_flavor,
            device_brand: t.device_brand,
            device_model: t.device_model,
            last_seen_at: t.last_seen_at,
            source: 'token',
          });
        }
      }
    }

    let delivery = 'targeted';
    if (fcmDelivered) delivery = 'fcm_ok';
    else if (fcmOk) delivery = 'fcm_sent';
    else if (fcmFailed) delivery = 'fcm_fail';
    else if (m.quoted || m.assigned || m.chatted) delivery = 'seen_app';
    else if (!m.notified) delivery = 'not_sent';

    return {
      ...m,
      devices: deviceFromSends.length ? deviceFromSends : liveDevices,
      fcm_logged: fcmLogged,
      fcm_ok: fcmOk,
      fcm_failed: fcmFailed,
      wa_notified: waPhoneByMech.has(mid) || waByPhone.has(String(m.phone || '').replace(/\D/g, '').slice(-10)),
      wa_phone: waPhoneByMech.get(mid) || (waByPhone.has(String(m.phone || '').replace(/\D/g, '').slice(-10))
        ? String(m.phone || '').replace(/\D/g, '').slice(-10)
        : null),
      delivery,
    };
  });
};

const partnerInArea = (mech, jobLat, jobLng, address) => {
  const radius = Math.max(1, Math.min(100, Number(mech.service_radius_km) || 15));
  const km = haversineKm(mech.service_center_lat, mech.service_center_lng, jobLat, jobLng);
  if (km != null) return { inArea: km <= radius, distanceKm: km };
  const addr = String(address || '').toLowerCase();
  const city = String(mech.city || '').trim().toLowerCase();
  const area = String(mech.area || '').trim().toLowerCase();
  if (jobLat != null && jobLng != null) return { inArea: false, distanceKm: null };
  const textHit =
    (city.length >= 3 && addr.includes(city)) || (area.length >= 3 && addr.includes(area));
  return { inArea: textHit, distanceKm: null };
};

const partnerLabel = (m, userName) => {
  const shop = String(m?.shop_name || m?.mechanic_name || m?.name || '').trim();
  const person = String(userName || m?.mechanic_name || '').trim();
  if (shop && person && shop.toLowerCase() !== person.toLowerCase()) return `${shop} · ${person}`;
  return shop || person || 'Partner';
};

async function attachChatActivity(requestId, mechanics) {
  const db = dbClient();
  const { data: chatRows } = await db
    .from('rsa_job_messages')
    .select('mechanic_id, sender_role, sender_user_id, created_at')
    .eq('service_request_id', requestId)
    .eq('sender_role', 'partner')
    .order('created_at', { ascending: false })
    .limit(200);

  const lastByMech = new Map();
  const senderByMech = new Map();
  for (const row of chatRows || []) {
    const id = String(row.mechanic_id || '').trim();
    if (!id || lastByMech.has(id)) continue;
    lastByMech.set(id, row.created_at);
    if (row.sender_user_id) senderByMech.set(id, row.sender_user_id);
  }

  const existing = new Map((mechanics || []).map((m) => [String(m.mechanic_id), m]));
  const missingIds = Array.from(lastByMech.keys()).filter((id) => !existing.has(id));
  if (missingIds.length) {
    const { data: extra } = await db
      .from('mechanics')
      .select(
        'id, shop_name, mechanic_name, number, alternate_number1, city, area, active',
      )
      .in('id', missingIds);
    for (const m of extra || []) {
      existing.set(String(m.id), {
        mechanic_id: m.id,
        name: m.shop_name || m.mechanic_name || 'Partner',
        shop_name: m.shop_name,
        mechanic_name: m.mechanic_name,
        phone: m.number || m.alternate_number1 || null,
        city: m.city,
        area: m.area,
        active: !!m.active,
        in_area: true,
        notified: true,
        offline: false,
        quoted: false,
        assigned: false,
        distance_km: null,
      });
    }
  }

  const senderIds = Array.from(new Set(senderByMech.values()));
  const userNameById = new Map();
  if (senderIds.length) {
    const { data: users } = await db.from('users').select('id, name').in('id', senderIds);
    for (const u of users || []) {
      userNameById.set(String(u.id), String(u.name || '').trim());
    }
  }

  const merged = Array.from(existing.values()).map((m) => {
    const mid = String(m.mechanic_id);
    const chatted = lastByMech.has(mid);
    const senderName = userNameById.get(String(senderByMech.get(mid) || '')) || '';
    return {
      ...m,
      chatted,
      chatted_at: lastByMech.get(mid) || null,
      chat_person: senderName || null,
      name: chatted ? partnerLabel(m, senderName) : m.name,
    };
  });

  merged.sort(
    (a, b) =>
      Number(!!b.chatted) - Number(!!a.chatted) ||
      Number(!!b.quoted) - Number(!!a.quoted) ||
      Number(!!b.notified) - Number(!!a.notified) ||
      (a.distance_km ?? 9999) - (b.distance_km ?? 9999),
  );
  return merged;
}

async function loadNotifiedMechanics(requestId) {
  const db = dbClient();
  try {
    const { data, error } = await db.rpc('admin_list_rsa_notified_mechanics', {
      p_request_id: requestId,
    });
    if (!error && data?.success !== false && Array.isArray(data?.mechanics)) {
      const withChat = await attachChatActivity(requestId, data.mechanics.map((m) => ({
        ...m,
        in_area: m.in_area != null ? !!m.in_area : m.notified ? (m.distance_km == null || Number(m.distance_km) <= 80) : !!m.in_area,
        offline: m.offline != null ? !!m.offline : !!m.in_area && !m.notified,
      })));
      const rows = await attachPushDevices(requestId, withChat);
      try {
        const { data: jobRow } = await db
          .from('service_requests')
          .select('service_type')
          .eq('id', requestId)
          .maybeSingle();
        const wa = await sendMissingPartnerJobNotifyWhatsApp(
          requestId,
          rows,
          jobRow?.service_type || data?.service_type,
        );
        if (wa?.sent) {
          for (const m of rows) {
            if (m.notified && !m.wa_notified && String(m.phone || '').replace(/\D/g, '').slice(-10)) {
              m.wa_notified = true;
              m.wa_phone = String(m.phone || '').replace(/\D/g, '').slice(-10);
            }
          }
        }
      } catch (e) {
        console.warn('sendMissingPartnerJobNotifyWhatsApp', e);
      }
      return {
        mechanics: rows,
        hasPushLog: !!data.has_push_log,
        notifiedInArea: Number(data.notified_in_area ?? rows.filter((m) => m.notified && m.in_area).length),
        offlineInArea: Number(data.offline_in_area ?? rows.filter((m) => m.offline).length),
        notifiedOutOfArea: Number(data.notified_out_of_area ?? rows.filter((m) => m.notified && !m.in_area).length),
        error: null,
      };
    }
    if (error && !/could not find the function|schema cache|PGRST202/i.test(error.message || '')) {
      console.warn('admin_list_rsa_notified_mechanics', error.message);
    }
  } catch (e) {
    console.warn('admin_list_rsa_notified_mechanics', e);
  }

  const [targetsRes, offersRes, jobRes] = await Promise.all([
    db
      .from('partner_job_bell_targets')
      .select('mechanic_id, bell_started_at, created_at')
      .eq('request_id', requestId),
    db
      .from('service_request_rate_offers')
      .select('mechanic_id, amount, status, created_at, proposed_by')
      .eq('service_request_id', requestId)
      .in('proposed_by', ['mechanic', 'crew']),
    db
      .from('service_requests')
      .select('pickup_latitude, pickup_longitude, pickup_address, assigned_mechanic_id, service_type')
      .eq('id', requestId)
      .maybeSingle(),
  ]);

  const job = jobRes.data || {};
  const jobLat = job.pickup_latitude;
  const jobLng = job.pickup_longitude;
  const byId = new Map();
  const add = (id, patch) => {
    const key = String(id || '').trim();
    if (!key) return;
    const prev = byId.get(key) || { mechanic_id: key, notified: false, quoted: false };
    byId.set(key, { ...prev, ...patch });
  };

  for (const t of targetsRes.data || []) {
    add(t.mechanic_id, {
      notified: true,
      notified_at: t.created_at || t.bell_started_at,
    });
  }
  for (const o of offersRes.data || []) {
    add(o.mechanic_id, {
      quoted: true,
      quote_amount: o.amount,
      quote_status: o.status,
    });
  }

  const { data: pool } = await db
    .from('mechanics')
    .select(
      'id, shop_name, mechanic_name, number, alternate_number1, city, area, active, owner_user_id, service_center_lat, service_center_lng, service_radius_km, service_tag, service_tag2, service_tag3',
    )
    .not('owner_user_id', 'is', null)
    .limit(4000);

  (pool || []).forEach((m) => {
    const area = partnerInArea(m, jobLat, jobLng, job.pickup_address);
    const specOk = partnerMatchesJobSpec(m, job.service_type);
    if (area.inArea && specOk) add(m.id, { in_area: true, distance_km: area.distanceKm });
    else if (byId.has(String(m.id))) add(m.id, { in_area: false, distance_km: area.distanceKm });
  });

  const ids = Array.from(byId.keys());
  if (!ids.length) {
    return { mechanics: [], hasPushLog: false, error: null };
  }

  const { data: mechs } = await db
    .from('mechanics')
    .select(
      'id, shop_name, mechanic_name, number, alternate_number1, city, area, active, service_center_lat, service_center_lng, service_radius_km',
    )
    .in('id', ids);

  const mechanics = (mechs || []).map((m) => {
    const extra = byId.get(String(m.id)) || {};
    const area = partnerInArea(m, jobLat, jobLng, job.pickup_address);
    const inArea = extra.in_area != null ? extra.in_area : area.inArea;
    const notified = !!extra.notified;
    return {
      mechanic_id: m.id,
      name: m.shop_name || m.mechanic_name || 'Partner',
      shop_name: m.shop_name,
      mechanic_name: m.mechanic_name,
      phone: m.number || m.alternate_number1 || null,
      city: m.city,
      area: m.area,
      active: !!m.active,
      in_area: inArea,
      notified,
      offline: inArea && !notified,
      notified_at: extra.notified_at || null,
      quoted: !!extra.quoted,
      quote_amount: extra.quote_amount ?? null,
      quote_status: extra.quote_status || null,
      assigned: job.assigned_mechanic_id === m.id,
      distance_km: extra.distance_km != null ? extra.distance_km : area.distanceKm,
    };
  });
  mechanics.sort(
    (a, b) =>
      Number(b.quoted) - Number(a.quoted) ||
      Number(b.notified) - Number(a.notified) ||
      (a.distance_km ?? 9999) - (b.distance_km ?? 9999),
  );
  const withChat = await attachChatActivity(requestId, mechanics);
  const withDevices = await attachPushDevices(requestId, withChat);
  try {
    const wa = await sendMissingPartnerJobNotifyWhatsApp(requestId, withDevices, job.service_type);
    if (wa?.sent) {
      for (const m of withDevices) {
        if (m.notified && !m.wa_notified && String(m.phone || '').replace(/\D/g, '').slice(-10)) {
          m.wa_notified = true;
          m.wa_phone = String(m.phone || '').replace(/\D/g, '').slice(-10);
        }
      }
    }
  } catch (e) {
    console.warn('sendMissingPartnerJobNotifyWhatsApp', e);
  }
  return {
    mechanics: withDevices,
    hasPushLog: (targetsRes.data || []).length > 0,
    notifiedInArea: withChat.filter((m) => m.notified && m.in_area).length,
    offlineInArea: withChat.filter((m) => m.offline).length,
    notifiedOutOfArea: withChat.filter((m) => m.notified && !m.in_area).length,
    error: null,
  };
}

async function attachRouteDistances(requestId, payload) {
  const mechanics = payload?.mechanics || [];
  if (!requestId || !mechanics.length) return payload;
  try {
    const db = dbClient();
    const ids = [...new Set(mechanics.map((m) => m.mechanic_id).filter(Boolean))];
    const [{ data: job }, { data: shops }] = await Promise.all([
      db
        .from('service_requests')
        .select('pickup_latitude, pickup_longitude')
        .eq('id', requestId)
        .maybeSingle(),
      ids.length
        ? db.from('mechanics').select('id, service_center_lat, service_center_lng').in('id', ids)
        : Promise.resolve({ data: [] }),
    ]);
    const origin = { lat: job?.pickup_latitude, lng: job?.pickup_longitude };
    const dests = (shops || []).map((m) => ({
      id: m.id,
      lat: m.service_center_lat,
      lng: m.service_center_lng,
    }));
    const byId = await drivingDistancesById(origin, dests);
    const next = mechanics.map((m) => {
      const route = byId[String(m.mechanic_id)];
      if (!route) return m;
      return {
        ...m,
        distance_km: route.km,
        distance_min: route.min,
        distance_is_route: true,
      };
    });
    next.sort(
      (a, b) =>
        Number(!!b.chatted) - Number(!!a.chatted) ||
        Number(!!b.quoted) - Number(!!a.quoted) ||
        Number(!!b.notified) - Number(!!a.notified) ||
        (a.distance_km ?? 9999) - (b.distance_km ?? 9999),
    );
    return { ...payload, mechanics: next };
  } catch (e) {
    console.warn('attachRouteDistances', e);
    return payload;
  }
}

const RsaNotifiedMechanics = ({ requestId }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadNotifiedMechanics(requestId);
      setPayload(data);
      setLoading(false);
      setPayload(await attachRouteDistances(requestId, data));
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = () => setOpen((v) => !v);

  const mechanics = payload?.mechanics || [];
  const chatted = mechanics.filter((m) => m.chatted);
  const notifiedInArea = mechanics.filter((m) => m.notified && m.in_area && !m.chatted);
  const offlineInArea = mechanics.filter((m) => m.offline || (m.in_area && !m.notified));
  const notifiedOut = mechanics.filter((m) => m.notified && !m.in_area);
  const notifiedCount = payload?.notifiedInArea ?? notifiedInArea.length;
  const offlineCount = payload?.offlineInArea ?? offlineInArea.length;
  const outCount = payload?.notifiedOutOfArea ?? notifiedOut.length;

  const deliveryBadge = (m, kind) => {
    if (m.chatted || m.quoted || m.assigned || m.delivery === 'seen_app') {
      return (
        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-800">
          Seen in app
        </span>
      );
    }
    if (m.delivery === 'fcm_ok') {
      return (
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800">
          FCM accepted
        </span>
      );
    }
    if (m.delivery === 'fcm_sent') {
      return (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
          FCM sent
        </span>
      );
    }
    if (m.delivery === 'fcm_fail') {
      return (
        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-800">
          FCM failed
        </span>
      );
    }
    if (kind === 'notified' || m.notified) {
      return (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
          Targeted · FCM log nahi
        </span>
      );
    }
    return null;
  };

  const renderRow = (m, kind) => (
    <li
      key={`${kind}-${m.mechanic_id}`}
      className="space-y-1.5 rounded-md bg-white px-2.5 py-2 text-xs"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{m.name}</p>
          <p className="text-slate-500">
            {[m.phone, m.area, m.city].filter(Boolean).join(' · ') || '—'}
            {m.distance_km != null ? (
              <span title={m.distance_is_route ? 'Driving route to customer' : 'Straight-line distance'}>
                {` · ${m.distance_km} km`}
                {m.distance_is_route ? ' via road' : ''}
                {m.distance_min != null ? ` · ${m.distance_min} min` : ''}
              </span>
            ) : (
              ''
            )}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          {m.assigned ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800">
              Assigned
            </span>
          ) : null}
          {m.chatted ? (
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-800">
              App chat
            </span>
          ) : null}
          {kind === 'out' ? (
            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-800">
              Out of area
            </span>
          ) : kind === 'offline' ? (
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
              Offline
            </span>
          ) : m.notified || kind === 'notified' || kind === 'chat' ? (
            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-800">
              Notified
            </span>
          ) : null}
          {m.quoted ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
              Quoted{m.quote_amount != null ? ` ₹${Number(m.quote_amount)}` : ''}
            </span>
          ) : null}
          {m.wa_notified ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const digits = String(m.wa_phone || m.phone || '').replace(/\D/g, '');
                const phone10 = digits.length >= 10 ? digits.slice(-10) : digits;
                if (!phone10) return;
                navigate(`/whatsapp-automation?phone=${encodeURIComponent(phone10)}&filter=job_notify&name=${encodeURIComponent(m.name || '')}`);
              }}
              className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 hover:bg-emerald-100"
              title="Open WA job SMS chat"
            >
              WA job SMS
            </button>
          ) : null}
          {deliveryBadge(m, kind)}
        </div>
      </div>
      {Array.isArray(m.devices) && m.devices.length ? (
        <ul className="space-y-1 border-t border-slate-100 pt-1.5">
          {m.devices.map((d, i) => (
            <li key={`${m.mechanic_id}-dev-${i}`} className="flex items-start gap-1.5 text-[11px] text-slate-600">
              <Smartphone size={12} className="mt-0.5 shrink-0 text-slate-400" />
              <span>
                <span className="font-semibold text-slate-800">{d.who}</span>
                {d.name ? ` · ${d.name}` : ''}
                {' · '}
                {d.source === 'no_token'
                  ? 'Is account pe koi Partner app device token nahi'
                  : [
                      platformLabel(d.platform),
                      deviceModelLabel(d),
                      flavorLabel(d.app_flavor),
                      deviceModelLabel(d) ? null : shortDeviceId(d.device_id),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                {d.last_seen_at ? ` · last seen ${fmtDate(d.last_seen_at)}` : ''}
                {d.source === 'fcm_log'
                  ? (d.delivered ? ' · FCM accepted' : d.ok ? ' · FCM sent' : ' · FCM failed')
                  : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : kind === 'notified' ? (
        <p className="text-[11px] text-slate-400">Device token nahi mila — push confirm nahi ho sakta.</p>
      ) : null}
    </li>
  );

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/80">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="inline-flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-700">
          <Bell size={14} className="text-violet-600" />
          Is area ke partners
          {payload ? (
            <>
              <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-800">
                {notifiedCount} notified
              </span>
              {chatted.length ? (
                <span className="rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
                  {chatted.length} chat
                </span>
              ) : null}
              <span className="rounded-md bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                {offlineCount} offline
              </span>
              {outCount > 0 ? (
                <span className="rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-800">
                  {outCount} out of area
                </span>
              ) : null}
            </>
          ) : null}
        </span>
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
      </button>
      {open ? (
        <div className="border-t border-slate-200 px-3 py-2">
          {loading ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : mechanics.length === 0 ? (
            <p className="text-xs text-slate-500">
              Is pickup area mein koi linked partner nahi mila (GPS radius empty).
            </p>
          ) : (
            <div className="max-h-80 space-y-3 overflow-y-auto">
              <p className="text-[11px] leading-relaxed text-slate-500">
                App chat / quote = partner ne job app mein dekhi. FCM accepted = phone ne notification
                receive ki. FCM sent = Google ne push accept kiya, device confirm nahi (logout / token
                stale). Targeted · FCM log nahi = ring target mila, send log nahi.
              </p>
              {chatted.length ? (
                <div>
                  <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-sky-700">
                    App chat hua ({chatted.length})
                  </p>
                  <ul className="space-y-2">{chatted.map((m) => renderRow(m, 'chat'))}</ul>
                </div>
              ) : null}
              <div>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-violet-700">
                  Notify gaya ({notifiedInArea.length})
                </p>
                {notifiedInArea.length === 0 ? (
                  <p className="text-[11px] text-slate-500">Is area ke kisi online partner ko ring nahi gaya.</p>
                ) : (
                  <ul className="space-y-2">{notifiedInArea.map((m) => renderRow(m, 'notified'))}</ul>
                )}
              </div>
              <div>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                  Area mein offline ({offlineInArea.length})
                </p>
                {offlineInArea.length === 0 ? (
                  <p className="text-[11px] text-slate-500">Is radius ke saare linked partners online the / notify ho gaye.</p>
                ) : (
                  <ul className="space-y-2">{offlineInArea.map((m) => renderRow(m, 'offline'))}</ul>
                )}
              </div>
              {notifiedOut.length > 0 ? (
                <div>
                  <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-rose-700">
                    Area ke bahar notify ({notifiedOut.length})
                  </p>
                  <ul className="space-y-2">{notifiedOut.map((m) => renderRow(m, 'out'))}</ul>
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};


const DetailField = ({ label, children }) => (
  <div className="py-2.5 border-b border-slate-100 last:border-0">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
    <div className="text-sm text-slate-800 break-words">{children || '—'}</div>
  </div>
);

const EnquiryDetailDrawer = ({ tab, row, onClose, children }) => {
  if (!row) return null;
  const title =
    tab === 'website'
      ? row.name || 'Website lead'
      : row.customer_name || 'Enquiry details';
  const phone = tab === 'website' ? row.phone : row.customer_phone;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" className="absolute inset-0 bg-black/35" onClick={onClose} aria-label="Close" />
      <aside className="relative w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col max-h-full">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {TABS.find((t) => t.id === tab)?.label || 'Enquiry'}
            </p>
            <h2 className="text-lg font-bold text-slate-900 truncate">{title}</h2>
            {phone ? (
              <a href={`tel:${phone}`} className="text-sm text-primary-600 hover:underline">
                {phone}
              </a>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg border border-slate-200 hover:bg-white text-slate-500"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">{children}</div>
      </aside>
    </div>
  );
};

/** Stamp / merge WhatsApp-linked RSA jobs so they show in RSA even if RPC is stale. */
async function enrichRsaRowsWithWhatsApp(rows, search, offset) {
  const db = serviceRoleClient || supabase;
  const list = Array.isArray(rows) ? [...rows] : [];
  const byId = new Map(list.map((r) => [r.id, r]));
  const term = search ? search.replace(/[%_,]/g, '').trim() : '';

  try {
    const { data: sessions } = await db
      .from('whatsapp_rsa_sessions')
      .select(
        'id, contact_phone, service_request_id, service_type, pickup_address, vehicle_number, problem, updated_at, created_at',
      )
      .not('service_request_id', 'is', null)
      .order('updated_at', { ascending: false })
      .limit(100);

    const jobIds = [...new Set((sessions || []).map((s) => s.service_request_id).filter(Boolean))];
    const sessByJob = Object.fromEntries(
      (sessions || []).filter((s) => s.service_request_id).map((s) => [s.service_request_id, s]),
    );

    // Mark existing RPC rows that are WA-linked
    list.forEach((row) => {
      if (sessByJob[row.id] || row.source === 'whatsapp_ai' || row.source === 'whatsapp_rsa') {
        row.from_whatsapp = true;
      }
    });

    // Only fetch missing WA jobs on first page (keeps pagination simple)
    if (offset > 0) {
      return { rows: list, extraTotal: 0 };
    }

    const missingIds = jobIds.filter((id) => !byId.has(id));
    let jobs = [];
    if (missingIds.length) {
      const { data } = await db
        .from('service_requests')
        .select(
          'id, customer_id, customer_name, contact_number, service_type, vehicle_number, pickup_address, pickup_latitude, pickup_longitude, location_link, request_status, complaint_status, source, pricing_mode, rate_status, customer_quoted_amount, admin_quoted_amount, agreed_amount, needs_admin_rate, open_for_partner_accept, assigned_mechanic_name, membership_subscription_id, problem, requested_at, complaint_registered_at, updated_at, delete_status',
        )
        .in('id', missingIds)
        .or('delete_status.is.null,delete_status.eq.false');
      jobs = data || [];
    }

    const pushJob = (job, sess) => {
      if (!job || byId.has(job.id) || job.delete_status) return 0;
      const row = {
        id: job.id,
        customer_name: job.customer_name || 'WhatsApp Customer',
        customer_phone: job.contact_number || sess?.contact_phone,
        service_type: job.service_type || sess?.service_type,
        vehicle_registration: job.vehicle_number || sess?.vehicle_number,
        pickup_address: job.pickup_address || sess?.pickup_address,
        pickup_latitude: job.pickup_latitude,
        pickup_longitude: job.pickup_longitude,
        location_link: job.location_link,
        request_status: job.request_status,
        complaint_status: job.complaint_status,
        source: job.source || 'whatsapp_rsa',
        pricing_mode: job.pricing_mode,
        rate_status: job.rate_status,
        customer_quoted_amount: job.customer_quoted_amount,
        admin_quoted_amount: job.admin_quoted_amount,
        agreed_amount: job.agreed_amount,
        needs_admin_rate: job.needs_admin_rate,
        open_for_partner_accept: job.open_for_partner_accept,
        assigned_mechanic_name: job.assigned_mechanic_name,
        problem: job.problem || sess?.problem,
        from_whatsapp: true,
        requested_at: job.requested_at,
        complaint_registered_at: job.complaint_registered_at,
        created_at: job.complaint_registered_at || job.requested_at || job.updated_at,
      };
      if (term) {
        const hay = [
          row.customer_name,
          row.customer_phone,
          row.vehicle_registration,
          row.service_type,
          row.pickup_address,
          row.problem,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(term.toLowerCase())) return 0;
      }
      list.push(row);
      byId.set(job.id, row);
      return 1;
    };

    let added = 0;
    (jobs || []).forEach((job) => {
      added += pushJob(job, sessByJob[job.id]);
    });

    // Also pull source=whatsapp_ai jobs not linked to a session (legacy drafts→jobs)
    const { data: waSrcJobs } = await db
      .from('service_requests')
      .select(
        'id, customer_id, customer_name, contact_number, service_type, vehicle_number, pickup_address, pickup_latitude, pickup_longitude, location_link, request_status, complaint_status, source, pricing_mode, rate_status, customer_quoted_amount, admin_quoted_amount, agreed_amount, needs_admin_rate, open_for_partner_accept, assigned_mechanic_name, membership_subscription_id, problem, requested_at, complaint_registered_at, updated_at, delete_status',
      )
      .eq('source', 'whatsapp_ai')
      .or('delete_status.is.null,delete_status.eq.false')
      .order('requested_at', { ascending: false })
      .limit(40);
    (waSrcJobs || []).forEach((job) => {
      added += pushJob(job, null);
    });

    if (added) {
      list.sort(
        (a, b) =>
          new Date(b.complaint_registered_at || b.requested_at || b.created_at || 0) -
          new Date(a.complaint_registered_at || a.requested_at || a.created_at || 0),
      );
    }
    return { rows: list, extraTotal: added };
  } catch (err) {
    console.warn('enrichRsaRowsWithWhatsApp:', err?.message || err);
    return { rows: list, extraTotal: 0 };
  }
}

const ENTERPRISE_JOB_SELECT =
  'id, customer_id, customer_name, contact_number, service_type, vehicle_number, vehicle_brand, vehicle_model, pickup_address, pickup_latitude, pickup_longitude, location_link, request_status, complaint_status, source, pricing_mode, rate_status, customer_quoted_amount, admin_quoted_amount, agreed_amount, needs_admin_rate, open_for_partner_accept, assigned_mechanic_id, assigned_mechanic_name, membership_subscription_id, problem, requested_at, complaint_registered_at, updated_at, delete_status, enterprise_id, enterprise_ref, enterprise_rate_pick_mode, scheduled_at, booking_mode';

/** Merge B2B complaints onto RSA even if admin_list_app_enquiries is stale. */
async function enrichRsaRowsWithEnterprise(rows, search, offset) {
  const db = serviceRoleClient || supabase;
  const list = Array.isArray(rows) ? [...rows] : [];
  const byId = new Map(list.map((r) => [r.id, r]));
  const term = search ? search.replace(/[%_,]/g, '').trim() : '';

  if (offset > 0) return { rows: list, extraTotal: 0 };

  try {
    const { data: jobs, error } = await db
      .from('service_requests')
      .select(`${ENTERPRISE_JOB_SELECT}, enterprises ( name )`)
      .or('source.in.(enterprise_manual,enterprise_api),enterprise_id.not.is.null')
      .or('delete_status.is.null,delete_status.eq.false')
      .order('requested_at', { ascending: false })
      .limit(40);

    if (error) {
      const retry = await db
        .from('service_requests')
        .select(ENTERPRISE_JOB_SELECT)
        .or('source.in.(enterprise_manual,enterprise_api),enterprise_id.not.is.null')
        .or('delete_status.is.null,delete_status.eq.false')
        .order('requested_at', { ascending: false })
        .limit(40);
      if (retry.error) throw retry.error;
      return mergeEnterpriseJobs(list, byId, retry.data || [], term);
    }

    return mergeEnterpriseJobs(list, byId, jobs || [], term);
  } catch (err) {
    console.warn('enrichRsaRowsWithEnterprise:', err?.message || err);
    return { rows: list, extraTotal: 0 };
  }
}

function mergeEnterpriseJobs(list, byId, jobs, term) {
  let added = 0;
  (jobs || []).forEach((job) => {
    if (!job || job.delete_status) return;
    const enterpriseName =
      job.enterprise_name ||
      job.enterprises?.name ||
      (Array.isArray(job.enterprises) ? job.enterprises[0]?.name : null);
    const existing = byId.get(job.id);
    if (existing) {
      existing.enterprise_id = existing.enterprise_id || job.enterprise_id;
      existing.enterprise_name = existing.enterprise_name || enterpriseName;
      existing.enterprise_ref = existing.enterprise_ref || job.enterprise_ref;
      existing.enterprise_rate_pick_mode =
        existing.enterprise_rate_pick_mode || job.enterprise_rate_pick_mode;
      existing.vehicle_brand = existing.vehicle_brand || job.vehicle_brand;
      existing.vehicle_model = existing.vehicle_model || job.vehicle_model;
      existing.scheduled_at = existing.scheduled_at || job.scheduled_at;
      existing.booking_mode = existing.booking_mode || job.booking_mode;
      existing.assigned_mechanic_id = existing.assigned_mechanic_id || job.assigned_mechanic_id;
      existing.assigned_mechanic_name = existing.assigned_mechanic_name || job.assigned_mechanic_name;
      existing.admin_quoted_amount = existing.admin_quoted_amount || job.admin_quoted_amount;
      existing.customer_quoted_amount = existing.customer_quoted_amount || job.customer_quoted_amount;
      return;
    }

    const row = {
      id: job.id,
      customer_name: job.customer_name || 'B2B Customer',
      customer_phone: job.contact_number,
      service_type: job.service_type,
      vehicle_registration: job.vehicle_number,
      vehicle_brand: job.vehicle_brand,
      vehicle_model: job.vehicle_model,
      pickup_address: job.pickup_address,
      pickup_latitude: job.pickup_latitude,
      pickup_longitude: job.pickup_longitude,
      location_link: job.location_link,
      request_status: job.request_status,
      complaint_status: job.complaint_status,
      source: job.source || 'enterprise_manual',
      pricing_mode: job.pricing_mode,
      rate_status: job.rate_status,
      customer_quoted_amount: job.customer_quoted_amount,
      admin_quoted_amount: job.admin_quoted_amount,
      agreed_amount: job.agreed_amount,
      needs_admin_rate: false,
      open_for_partner_accept: job.open_for_partner_accept,
      assigned_mechanic_id: job.assigned_mechanic_id,
      assigned_mechanic_name: job.assigned_mechanic_name,
      problem: job.problem,
      enterprise_id: job.enterprise_id,
      enterprise_name: enterpriseName,
      enterprise_ref: job.enterprise_ref,
      enterprise_rate_pick_mode: job.enterprise_rate_pick_mode,
      scheduled_at: job.scheduled_at,
      booking_mode: job.booking_mode,
      requested_at: job.requested_at,
      complaint_registered_at: job.complaint_registered_at,
      created_at: job.complaint_registered_at || job.requested_at || job.updated_at,
    };

    if (term) {
      const hay = [
        row.customer_name,
        row.customer_phone,
        row.vehicle_registration,
        row.vehicle_brand,
        row.vehicle_model,
        row.service_type,
        row.pickup_address,
        row.enterprise_name,
        row.enterprise_ref,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(term.toLowerCase())) return;
    }

    list.push(row);
    byId.set(job.id, row);
    added += 1;
  });

  if (added) {
    list.sort(
      (a, b) =>
        new Date(b.complaint_registered_at || b.requested_at || b.created_at || 0) -
        new Date(a.complaint_registered_at || a.requested_at || a.created_at || 0),
    );
  }
  return { rows: list, extraTotal: added };
}

async function enrichRsaRowsWithWebsite(rows, search, offset) {
  const db = serviceRoleClient || supabase;
  const list = Array.isArray(rows) ? [...rows] : [];
  const byId = new Map(list.map((r) => [r.id, r]));
  const term = search ? search.replace(/[%_,]/g, '').trim() : '';

  if (offset > 0) return { rows: list, extraTotal: 0 };

  try {
    const { data: jobs, error } = await db
      .from('service_requests')
      .select(ENTERPRISE_JOB_SELECT)
      .eq('source', 'website_rsa')
      .or('delete_status.is.null,delete_status.eq.false')
      .order('complaint_registered_at', { ascending: false, nullsFirst: false })
      .limit(40);
    if (error) throw error;

    let added = 0;
    (jobs || []).forEach((job) => {
      if (!job || byId.has(job.id) || job.delete_status) return;
      const row = {
        id: job.id,
        customer_name: job.customer_name || 'Website customer',
        customer_phone: job.contact_number,
        service_type: job.service_type,
        vehicle_registration: job.vehicle_number,
        pickup_address: job.pickup_address,
        pickup_latitude: job.pickup_latitude,
        pickup_longitude: job.pickup_longitude,
        location_link: job.location_link,
        request_status: job.request_status,
        complaint_status: job.complaint_status,
        source: job.source || 'website_rsa',
        pricing_mode: job.pricing_mode,
        rate_status: job.rate_status,
        customer_quoted_amount: job.customer_quoted_amount,
        admin_quoted_amount: job.admin_quoted_amount,
        agreed_amount: job.agreed_amount,
        needs_admin_rate: job.needs_admin_rate,
        open_for_partner_accept: job.open_for_partner_accept,
        assigned_mechanic_id: job.assigned_mechanic_id,
        assigned_mechanic_name: job.assigned_mechanic_name,
        problem: job.problem,
        requested_at: job.requested_at,
        complaint_registered_at: job.complaint_registered_at,
        created_at: job.complaint_registered_at || job.requested_at || job.updated_at,
      };
      if (term) {
        const hay = [
          row.customer_name,
          row.customer_phone,
          row.vehicle_registration,
          row.service_type,
          row.pickup_address,
          row.problem,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(term.toLowerCase())) return;
      }
      list.push(row);
      byId.set(job.id, row);
      added += 1;
    });

    if (added) {
      list.sort(
        (a, b) =>
          new Date(b.complaint_registered_at || b.requested_at || b.created_at || 0) -
          new Date(a.complaint_registered_at || a.requested_at || a.created_at || 0),
      );
    }
    return { rows: list, extraTotal: added };
  } catch (err) {
    console.warn('enrichRsaRowsWithWebsite:', err?.message || err);
    return { rows: list, extraTotal: 0 };
  }
}

async function fetchEnquiryPage(tab, search, offset = 0, rsaStatus = 'all', rsaSource = 'all') {
  const safeOffset = Math.max(0, Number(offset) || 0);

  if (tab === 'rsa' && ((rsaStatus && rsaStatus !== 'all') || (rsaSource && rsaSource !== 'all'))) {
    return fetchRsaFilteredPage(search, safeOffset, rsaStatus, rsaSource);
  }

  if (tab === 'website') {
    let query = marketingAdmin
      .from('website_leads')
      .select('*', { count: 'exact' })
      .or(WEBSITE_LEADS_EXCLUDE_RSA)
      .order('created_at', { ascending: false })
      .range(safeOffset, safeOffset + PAGE_SIZE - 1);

    if (search) {
      const term = search.replace(/[%_]/g, '');
      query = query.or(`name.ilike.%${term}%,phone.ilike.%${term}%,message.ilike.%${term}%`);
    }

    const { data: rows, error, count } = await query;
    if (error) throw error;
    return { rows: Array.isArray(rows) ? rows : [], total: Number(count) || 0, offset: safeOffset };
  }

  const res = await AdminService.getAppEnquiries(tab, {
    search,
    limit: PAGE_SIZE,
    offset: safeOffset,
  });
  if (!res.success && res.error) throw new Error(res.error);

  const rpcRows = Array.isArray(res.rows) ? res.rows : [];
  const rpcTotal = Number(res.total) || 0;
  const fetched = rpcRows.length;
  let rows = rpcRows;

  if (tab === 'rsa') {
    const wa = await enrichRsaRowsWithWhatsApp(rows, search, safeOffset);
    rows = wa.rows;
    const web = await enrichRsaRowsWithWebsite(rows, search, safeOffset);
    rows = web.rows;
    const b2b = await enrichRsaRowsWithEnterprise(rows, search, safeOffset);
    rows = await attachEnterpriseNames(
      b2b.rows.filter((row) => !isRsaScheduledExpiredUnaccepted(row)),
    );
  }

  return {
    rows,
    total: rpcTotal,
    offset: safeOffset,
    fetched,
  };
}

async function countUnreadSince(tab, sinceIso) {
  if (!sinceIso) return 0;
  const since = new Date(sinceIso).getTime();
  if (!Number.isFinite(since)) return 0;

  try {
    if (tab === 'website') {
      const { count, error } = await marketingAdmin
        .from('website_leads')
        .select('id', { count: 'exact', head: true })
        .or(WEBSITE_LEADS_EXCLUDE_RSA)
        .gt('created_at', sinceIso);
      if (error) return 0;
      return Number(count) || 0;
    }

    // Lightweight unread estimate: first page of recent rows vs last-seen
    const res = await AdminService.getAppEnquiries(tab, { limit: 40, offset: 0 });
    const rows = res.rows || [];
    return rows.filter((r) => {
      const ts = new Date(rowCreatedAt(tab, r) || 0).getTime();
      return Number.isFinite(ts) && ts > since;
    }).length;
  } catch {
    return 0;
  }
}

const AppEnquiries = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('rsa');
  const [rsaStatus, setRsaStatus] = useState('all');
  const [rsaSource, setRsaSource] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [updating, setUpdating] = useState({});
  const [rateDrafts, setRateDrafts] = useState({});
  const [detailRow, setDetailRow] = useState(null);
  const [unread, setUnread] = useState({
    car_loan: 0,
    car_service: 0,
    rsa: 0,
    website: 0,
  });
  const notifiedIdsRef = useRef(new Set());
  const activeTabRef = useRef(activeTab);
  const loadMoreRef = useRef(null);
  const seenRef = useRef(loadSeenMap());

  activeTabRef.current = activeTab;

  const markTabSeen = useCallback((tabId) => {
    const next = { ...seenRef.current, [tabId]: new Date().toISOString() };
    seenRef.current = next;
    saveSeenMap(next);
    setUnread((prev) => ({ ...prev, [tabId]: 0 }));
  }, []);

  const bumpUnread = useCallback((tabId) => {
    if (activeTabRef.current === tabId) return;
    setUnread((prev) => ({ ...prev, [tabId]: (prev[tabId] || 0) + 1 }));
  }, []);

  useEffect(() => {
    markTabSeen(activeTab);
  }, [activeTab, markTabSeen]);

  // Initial unread from last-seen timestamps
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const seen = seenRef.current;
      const next = {};
      await Promise.all(
        TABS.map(async (tab) => {
          if (tab.id === activeTabRef.current) {
            next[tab.id] = 0;
            return;
          }
          next[tab.id] = await countUnreadSince(tab.id, seen[tab.id]);
        }),
      );
      if (!cancelled) setUnread((prev) => ({ ...prev, ...next }));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ['adminAppEnquiries', activeTab, search, rsaStatus, rsaSource],
    queryFn: ({ pageParam = 0 }) =>
      fetchEnquiryPage(
        activeTab,
        search,
        pageParam ?? 0,
        activeTab === 'rsa' ? rsaStatus : 'all',
        activeTab === 'rsa' ? rsaSource : 'all',
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      // Use RPC page size, not enriched card count. Extra B2B/WA rows on page 1
      // used to inflate the next offset and skip older completed jobs.
      const fetched = Number.isFinite(Number(lastPage.fetched))
        ? Number(lastPage.fetched)
        : Array.isArray(lastPage.rows)
          ? lastPage.rows.length
          : 0;
      if (fetched < PAGE_SIZE) return undefined;
      const nextOffset = Number(lastPage.offset || 0) + fetched;
      const total = Number(lastPage.total || 0);
      if (total > 0 && nextOffset >= total) return undefined;
      return nextOffset;
    },
    // Backup when realtime is delayed / not replicated
    refetchInterval: 12_000,
    refetchOnWindowFocus: true,
  });

  const rows = useMemo(() => {
    const seen = new Set();
    const list = [];
    (data?.pages || []).forEach((p) => {
      (Array.isArray(p?.rows) ? p.rows : []).forEach((row) => {
        if (!row?.id || seen.has(row.id)) return;
        seen.add(row.id);
        list.push(row);
      });
    });
    return list.sort((a, b) => {
      const ta = new Date(rowCreatedAt(activeTab, a) || 0).getTime();
      const tb = new Date(rowCreatedAt(activeTab, b) || 0).getTime();
      return tb - ta;
    });
  }, [data, activeTab]);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Infinite scroll sentinel
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '480px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, activeTab, rows.length]);

  // Patch a row inside infinite-query cache (instant status without waiting for RPC)
  const patchEnquiryRow = useCallback(
    (tabId, id, patch) => {
      if (!id || !patch) return;
      queryClient.setQueriesData({ queryKey: ['adminAppEnquiries', tabId] }, (old) => {
        if (!old?.pages) return old;
        let changed = false;
        const pages = old.pages.map((page) => {
          if (!Array.isArray(page?.rows)) return page;
          const rowsNext = page.rows.map((row) => {
            if (row.id !== id) return row;
            changed = true;
            return { ...row, ...patch };
          });
          return changed ? { ...page, rows: rowsNext } : page;
        });
        return changed ? { ...old, pages } : old;
      });
    },
    [queryClient],
  );

  // Realtime: live status + list refresh (no manual refresh)
  useEffect(() => {
    let debounceTimer = null;
    const scheduleRefetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['adminAppEnquiries'],
          refetchType: 'active',
        });
        refetchRef.current?.();
      }, 250);
    };

    const channel = supabase
      .channel('admin-app-enquiries-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'car_loan_leads' },
        (payload) => {
          if (payload.eventType === 'INSERT') bumpUnread('car_loan');
          if (payload.eventType === 'UPDATE' && payload.new?.id) {
            patchEnquiryRow('car_loan', payload.new.id, {
              status: payload.new.status,
              updated_at: payload.new.updated_at,
            });
          }
          scheduleRefetch();
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'service_bookings' },
        (payload) => {
          const st = String(payload.new?.service_type || payload.old?.service_type || '');
          if (!st.startsWith('garage:')) return;
          if (payload.eventType === 'INSERT') bumpUnread('car_service');
          if (payload.eventType === 'UPDATE' && payload.new?.id) {
            patchEnquiryRow('car_service', payload.new.id, {
              status: payload.new.status,
              amount_paid: payload.new.amount_paid ?? payload.new.amount,
              notes: payload.new.notes,
              updated_at: payload.new.updated_at,
            });
          }
          if (activeTabRef.current === 'car_service') scheduleRefetch();
          else if (payload.eventType === 'INSERT') scheduleRefetch();
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'service_requests' },
        (payload) => {
          const row = payload.new || payload.old || {};
          const src = String(row.source || '');
          const isRsaEnquiry =
            src === 'customer_rsa_app' ||
            src === 'customer_rsa_bargain' ||
            src === 'customer_rsa_on_demand' ||
            src === 'whatsapp_ai' ||
            src === 'enterprise_manual' ||
            src === 'enterprise_api' ||
            !!row.enterprise_id ||
            src.includes('rsa') ||
            src.includes('whatsapp') ||
            src.includes('enterprise');

          // Still patch if this id is already on the RSA list (status may change without source in payload)
          const knownOnList =
            activeTabRef.current === 'rsa' &&
            !!payload.new?.id &&
            (queryClient
              .getQueriesData({ queryKey: ['adminAppEnquiries', 'rsa'] })
              .some(([, data]) =>
                (data?.pages || []).some((p) => (p?.rows || []).some((r) => r.id === payload.new.id)),
              ));

          if (!isRsaEnquiry && !knownOnList) return;

          if (payload.eventType === 'INSERT' && isRsaEnquiry) {
            bumpUnread('rsa');
          }

          if (payload.eventType === 'UPDATE' && payload.new?.id) {
            const n = payload.new;
            patchEnquiryRow('rsa', n.id, {
              request_status: n.request_status,
              complaint_status: n.complaint_status,
              rate_status: n.rate_status,
              pricing_mode: n.pricing_mode,
              needs_admin_rate: n.needs_admin_rate,
              customer_quoted_amount: n.customer_quoted_amount,
              admin_quoted_amount: n.admin_quoted_amount,
              agreed_amount: n.agreed_amount,
              open_for_partner_accept: n.open_for_partner_accept,
              assigned_mechanic_name: n.assigned_mechanic_name,
              membership_subscription_id: n.membership_subscription_id,
              pickup_address: n.pickup_address,
              pickup_latitude: n.pickup_latitude,
              pickup_longitude: n.pickup_longitude,
              location_link: n.location_link,
              service_type: n.service_type,
              updated_at: n.updated_at,
            });
          }
          if (activeTabRef.current === 'rsa' || payload.eventType === 'INSERT') {
            scheduleRefetch();
          }
        },
      )
      .subscribe();

    let websiteChannel;
    try {
      websiteChannel = marketingAdmin
        .channel('admin-website-leads-live')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'website_leads' },
          (payload) => {
            // rsa_web leads are shown as RSA jobs (website_rsa), not on this tab
            if (String(payload.new?.source || '') === 'rsa_web') return;
            if (payload.eventType === 'INSERT') bumpUnread('website');
            if (payload.eventType === 'UPDATE' && payload.new?.id) {
              patchEnquiryRow('website', payload.new.id, {
                status: payload.new.status,
                message: payload.new.message,
                updated_at: payload.new.updated_at,
              });
            }
            scheduleRefetch();
          },
        )
        .subscribe();
    } catch {
      websiteChannel = null;
    }

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
      if (websiteChannel) {
        try {
          marketingAdmin.removeChannel(websiteChannel);
        } catch {
          /* ignore */
        }
      }
    };
  }, [bumpUnread, patchEnquiryRow, queryClient]);

  // WhatsApp backfill for recent open items on current page
  useEffect(() => {
    if (activeTab === 'website' || !rows.length) return;
    const cutoff = Date.now() - 6 * 60 * 60 * 1000;
    rows.forEach((row) => {
      const closed = activeTab === 'rsa' ? isRsaClosed(row) : isSimpleEnquiryClosed(row);
      if (closed || !row.id) return;
      const createdAt = rowCreatedAt(activeTab, row);
      if (createdAt && new Date(createdAt).getTime() < cutoff) return;
      const key = `${activeTab}:${row.id}`;
      if (notifiedIdsRef.current.has(key)) return;
      notifiedIdsRef.current.add(key);
      notifyAppEnquiryWhatsApp(activeTab, row.id, null);
    });
  }, [rows, activeTab]);

  const openDetail = (row) => {
    if (activeTab === 'rsa') {
      navigate(`/service_advisor/complaints/${row.id}`);
      return;
    }
    setDetailRow(row);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleEnquiryAction = async (type, row, action) => {
    const label = action === 'complete' ? 'Complete' : 'Cancel';
    const typeLabel =
      type === 'car_loan' ? 'car loan enquiry' : type === 'car_service' ? 'car service booking' : 'RSA request';
    const message =
      action === 'complete'
        ? `Mark this ${typeLabel} for ${row.customer_name || 'customer'} as completed?`
        : type === 'rsa'
          ? `Cancel this ${typeLabel} for ${row.customer_name || 'customer'}? The service slot will be restored.`
          : `Cancel this ${typeLabel} for ${row.customer_name || 'customer'}?`;
    if (!window.confirm(message)) return;

    const note = window.prompt(`${label} note (optional):`, '') || null;

    setUpdating((prev) => ({ ...prev, [row.id]: action }));
    try {
      let res;
      if (type === 'car_loan') {
        res = await AdminService.updateCarLoanEnquiryStatus(row.id, action, note);
      } else if (type === 'car_service') {
        res = await AdminService.updateCarServiceEnquiryStatus(row.id, action, note);
      } else {
        res = await AdminService.updateRsaEnquiryStatus(row.id, action, note);
      }
      if (!res.success) {
        alert(res.error || `Failed to ${label.toLowerCase()} enquiry`);
        return;
      }
      await refetch();
    } finally {
      setUpdating((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    }
  };

  const handleWebsiteStatus = async (row, nextStatus) => {
    if (!row?.id || row.status === nextStatus) return;
    setUpdating((prev) => ({ ...prev, [row.id]: nextStatus }));
    try {
      const { error } = await marketingAdmin
        .from('website_leads')
        .update({ status: nextStatus })
        .eq('id', row.id);
      if (error) {
        alert(error.message || 'Failed to update status');
        return;
      }
      await refetch();
      if (detailRow?.id === row.id) {
        setDetailRow({ ...row, status: nextStatus });
      }
    } finally {
      setUpdating((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    }
  };

  const handleSetRsaRate = async (row) => {
    const raw = rateDrafts[row.id] ?? row.admin_quoted_amount ?? row.customer_quoted_amount ?? '';
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid rate in ₹');
      return;
    }
    setUpdating((prev) => ({ ...prev, [row.id]: 'rate' }));
    try {
      const res = await AdminService.setRsaEnquiryRate(row.id, amount);
      if (!res.success) {
        alert(res.error || 'Failed to save rate');
        return;
      }
      await refetch();
    } finally {
      setUpdating((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    }
  };

  const NameButton = ({ row, name, sub }) => (
    <button
      type="button"
      onClick={() => openDetail(row)}
      className="text-left group max-w-[220px]"
      title="Open details"
    >
      <div className="inline-flex items-center gap-1 font-semibold text-slate-900 group-hover:text-teal-700">
        <span className="truncate">{name || '—'}</span>
        <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 shrink-0 text-teal-600" />
      </div>
      {sub ? <div className="text-xs text-slate-500 mt-0.5">{sub}</div> : null}
    </button>
  );

  const renderCarLoanCards = () =>
    rows.map((row) => (
      <article
        key={row.id}
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-300 transition"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <NameButton row={row} name={row.customer_name} sub={row.customer_phone} />
            {row.customer_email ? <p className="text-xs text-slate-500">{row.customer_email}</p> : null}
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge value={row.status} />
            <span className="text-xs text-slate-500">{fmtDate(row.created_at)}</span>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-[11px] uppercase text-slate-400 font-semibold">PAN</p>
            <p className="font-mono text-slate-800">{row.pan_number || '—'}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase text-slate-400 font-semibold">Vehicle</p>
            <p className="text-slate-800">{row.vehicle_registration || '—'}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase text-slate-400 font-semibold">Income</p>
            <p className="text-slate-800">₹{Number(row.monthly_income || 0).toLocaleString('en-IN')}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase text-slate-400 font-semibold">Occupation</p>
            <p className="text-slate-800">{row.occupation || '—'}</p>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <EnquiryActionButtons
            closed={isSimpleEnquiryClosed(row)}
            updating={updating[row.id]}
            onComplete={() => handleEnquiryAction('car_loan', row, 'complete')}
            onCancel={() => handleEnquiryAction('car_loan', row, 'cancel')}
          />
        </div>
      </article>
    ));

  const renderCarServiceCards = () =>
    rows.map((row) => {
      const meta = parseGarageNotes(row.notes);
      const city = row.booking_city || meta.city;
      const slot = row.pickup_slot_label || meta.pickup_slot_label;
      const paid = row.amount_paid ?? row.amount ?? meta.amount_paid;
      const category = String(row.service_name || '').replace(/-/g, ' ');
      return (
        <article
          key={row.id}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-300 transition"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <NameButton row={row} name={row.customer_name} sub={row.customer_phone} />
              <p className="text-sm font-medium text-slate-800 capitalize">{category || 'Garage service'}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge value={row.status} />
              <span className="text-xs text-slate-500">{fmtDate(row.created_at)}</span>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-[11px] uppercase text-slate-400 font-semibold">Vehicle</p>
              <p className="font-semibold uppercase text-slate-900">{row.vehicle_registration || '—'}</p>
              <p className="text-xs text-slate-500">{fmtVehicleTitle(row) || ''}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-400 font-semibold">Package</p>
              <p className="text-slate-800">{row.package_name || row.package_key || '—'}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-400 font-semibold">City / Slot</p>
              <p className="text-slate-800">{city || '—'}</p>
              <p className="text-xs text-slate-500">{slot || ''}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-400 font-semibold">Paid</p>
              <p className="font-semibold text-slate-900">₹{Number(paid || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <EnquiryActionButtons
              closed={isSimpleEnquiryClosed(row)}
              updating={updating[row.id]}
              onComplete={() => handleEnquiryAction('car_service', row, 'complete')}
              onCancel={() => handleEnquiryAction('car_service', row, 'cancel')}
            />
          </div>
        </article>
      );
    });

  const renderRsaCards = () =>
    rows.map((row) => {
      const isMember = isMembershipRsa(row);
      const isB2b = isEnterpriseRsa(row);
      const quoted = Number(row.admin_quoted_amount || row.customer_quoted_amount || row.agreed_amount);
      const rateStatus = String(row.rate_status || '');
      const needsRate =
        !isMember &&
        !isRsaClosed(row) &&
        (
          !!row.needs_admin_rate ||
          rateStatus === 'awaiting_admin_quote' ||
          (isB2b && !(quoted > 0) &&
            ['', 'awaiting_mechanic_quote', 'negotiating'].includes(rateStatus))
        );
      const used = Number(row.membership_services_used ?? 0);
      const limit = Number(row.membership_services_limit ?? 2);
      const card = fmtMembershipCard(row.membership_card_number);

      return (
        <article
          key={row.id}
          className={`rounded-xl border bg-white p-4 shadow-sm transition ${
            needsRate ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1.5">
              <NameButton row={row} name={row.customer_name} sub={row.customer_phone} />
              {row.customer_email ? <p className="text-xs text-slate-500">{row.customer_email}</p> : null}
              {isB2b && enterpriseDisplayName(row) ? (
                <p className="text-xs font-semibold text-indigo-800">{enterpriseDisplayName(row)}</p>
              ) : null}
              <div className="flex flex-wrap gap-1.5">
                {row.from_whatsapp || row.source === 'whatsapp_ai' ? (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800">
                    WhatsApp AI
                  </span>
                ) : null}
                {isB2b ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-800">
                    <Building2 size={10} />
                    B2B{enterpriseDisplayName(row) ? ` · ${enterpriseDisplayName(row)}` : ''}
                  </span>
                ) : null}
                {isWebsiteRsa(row) ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800">
                    <Globe size={10} />
                    Website
                  </span>
                ) : null}
                {isRsaScheduledJob(row) ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800">
                    <Calendar size={10} />
                    Scheduled
                  </span>
                ) : null}
                {isMember ? (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800">
                    Membership covered
                  </span>
                ) : null}
                {needsRate ? (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800">
                    {isB2b ? 'Set rate' : 'City not listed — set rate'}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <div className="flex flex-wrap justify-end gap-1">
                <StatusBadge value={isRsaCompleted(row) ? 'completed' : row.request_status} />
                {!isRsaCompleted(row) &&
                row.complaint_status &&
                row.complaint_status !== row.request_status ? (
                  <StatusBadge value={row.complaint_status} />
                ) : null}
              </div>
              <span className="text-xs text-slate-500">
                {fmtDate(row.complaint_registered_at || row.requested_at)}
              </span>
            </div>
          </div>

          {isRsaScheduledJob(row) ? (
            <div className="mt-3 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 flex items-start gap-2">
              <Calendar size={16} className="text-sky-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] uppercase font-bold tracking-wide text-sky-800">Scheduled for</p>
                <p className="text-sm font-semibold text-slate-900">{fmtScheduledWhen(row.scheduled_at)}</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {isRsaScheduledAccepted(row)
                    ? `Accepted${row.assigned_mechanic_name ? ` · ${row.assigned_mechanic_name}` : ''}`
                    : isRsaScheduledExpiredUnaccepted(row)
                      ? 'Time passed · not accepted'
                      : 'Awaiting partner accept'}
                </p>
              </div>
            </div>
          ) : null}

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-[11px] uppercase text-slate-400 font-semibold">Service</p>
              <p className="font-medium text-slate-900">{row.service_type || '—'}</p>
              <p className="text-[11px] uppercase text-slate-400 font-semibold mt-2">Vehicle</p>
              <p className="font-semibold uppercase text-slate-900">{row.vehicle_registration || '—'}</p>
              <p className="text-xs text-slate-500">
                {[fmtVehicleTitle(row), row.vehicle_fuel_type].filter(Boolean).join(' · ')}
              </p>
              <p className="text-[11px] uppercase text-slate-400 font-semibold mt-2">Source</p>
              <p className="font-medium text-slate-900">{fmtEnquirySource(row)}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-400 font-semibold">Membership</p>
              {row.membership_plan_name || card ? (
                <>
                  <p className="font-medium text-slate-900">{row.membership_plan_name || 'RSA Plan'}</p>
                  {card ? <p className="text-xs font-mono text-slate-600">{card}</p> : null}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {row.membership_status ? <StatusBadge value={row.membership_status} /> : null}
                    {row.membership_payment_status ? <StatusBadge value={row.membership_payment_status} /> : null}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Services {used}/{limit} used
                    {row.membership_amount_paid != null
                      ? ` · Paid ₹${Number(row.membership_amount_paid).toLocaleString('en-IN')}`
                      : ''}
                  </p>
                </>
              ) : (
                <p className="text-slate-400">—</p>
              )}
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-400 font-semibold">Location</p>
              <RsaLocationBlock row={row} />
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-400 font-semibold">
                {isMember ? 'Pricing' : isB2b ? 'B2B rate' : 'Admin rate'}
              </p>
              {isMember ? (
                <p className="text-sm font-medium text-emerald-700">Covered by membership</p>
              ) : isRsaClosed(row) ? (
                <p className="text-slate-800">
                  {Number.isFinite(quoted) && quoted > 0
                    ? `₹${quoted.toLocaleString('en-IN')}`
                    : '—'}
                </p>
              ) : (
                <div className="mt-0.5 space-y-1">
                  {isB2b ? (
                    <p className="text-xs text-slate-500">
                      Mechanic can still quote. Save to set a RoadServe rate.
                    </p>
                  ) : null}
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-slate-500">₹</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      placeholder={needsRate ? 'Enter rate' : 'Rate'}
                      className="w-24 border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
                      value={rateDrafts[row.id] ?? (Number.isFinite(quoted) && quoted > 0 ? String(quoted) : '')}
                      onChange={(e) =>
                        setRateDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))
                      }
                    />
                    <button
                      type="button"
                      onClick={() => handleSetRsaRate(row)}
                      disabled={updating[row.id] === 'rate'}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      {updating[row.id] === 'rate' ? '...' : 'Save'}
                    </button>
                  </div>
                  {row.enterprise_rate_pick_mode ? (
                    <p className="text-xs text-slate-500 capitalize">
                      {String(row.enterprise_rate_pick_mode).replace(/_/g, ' ')}
                    </p>
                  ) : null}
                  {row.assigned_mechanic_name ? (
                    <p className="text-xs text-slate-600">{row.assigned_mechanic_name}</p>
                  ) : null}
                </div>
              )}
              {!isMember && row.rate_status ? (
                <div className="mt-1">
                  <StatusBadge value={row.rate_status} />
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
            <ComplaintJobChat
              requestId={row.id}
              phone={row.customer_phone}
              customerName={row.customer_name}
              assignedMechanicId={row.assigned_mechanic_id}
              embedded
              defaultTab="support"
            />
          </div>
          <RsaNotifiedMechanics requestId={row.id} />

          <div className="mt-3 flex justify-end">
            <EnquiryActionButtons
              closed={isRsaClosed(row)}
              updating={updating[row.id]}
              onComplete={() => handleEnquiryAction('rsa', row, 'complete')}
              onCancel={() => handleEnquiryAction('rsa', row, 'cancel')}
            />
          </div>
        </article>
      );
    });

  const renderWebsiteCards = () =>
    rows.map((row) => (
      <article
        key={row.id}
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-300 transition"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <NameButton row={row} name={row.name} sub={row.phone} />
            <p className="text-xs text-slate-500 mt-1">
              {[row.city, row.service, row.source || 'website'].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge value={row.status || 'new'} />
            <span className="text-xs text-slate-500">{fmtDate(row.created_at)}</span>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap line-clamp-3">{row.message || '—'}</p>
        <div className="mt-3 flex justify-end">
          <select
            value={row.status || 'new'}
            disabled={!!updating[row.id]}
            onChange={(e) => handleWebsiteStatus(row, e.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
          >
            {WEBSITE_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </article>
    ));

  const renderDetailBody = () => {
    if (!detailRow) return null;
    if (activeTab === 'car_loan') {
      const row = detailRow;
      return (
        <>
          <DetailField label="Email">{row.customer_email}</DetailField>
          <DetailField label="City / Pincode">
            {[row.customer_city, row.customer_pincode].filter(Boolean).join(' · ') || '—'}
          </DetailField>
          <DetailField label="Address">{row.customer_address}</DetailField>
          <DetailField label="PAN">{row.pan_number}</DetailField>
          <DetailField label="Vehicle">{row.vehicle_registration}</DetailField>
          <DetailField label="Monthly income">
            ₹{Number(row.monthly_income || 0).toLocaleString('en-IN')}
          </DetailField>
          <DetailField label="Occupation">{row.occupation}</DetailField>
          <DetailField label="Status"><StatusBadge value={row.status} /></DetailField>
          <DetailField label="Submitted">{fmtDate(row.created_at)}</DetailField>
          <div className="pt-4">
            <EnquiryActionButtons
              closed={isSimpleEnquiryClosed(row)}
              updating={updating[row.id]}
              onComplete={() => handleEnquiryAction('car_loan', row, 'complete')}
              onCancel={() => handleEnquiryAction('car_loan', row, 'cancel')}
            />
          </div>
        </>
      );
    }
    if (activeTab === 'car_service') {
      const row = detailRow;
      const meta = parseGarageNotes(row.notes);
      const paid = row.amount_paid ?? row.amount ?? meta.amount_paid;
      return (
        <>
          <DetailField label="Email">{row.customer_email}</DetailField>
          <DetailField label="Vehicle">
            {[row.vehicle_registration, fmtVehicleTitle(row), row.vehicle_fuel_type]
              .filter(Boolean)
              .join(' · ') || '—'}
          </DetailField>
          <DetailField label="Service">{String(row.service_name || '').replace(/-/g, ' ')}</DetailField>
          <DetailField label="Package">{row.package_name || row.package_key}</DetailField>
          <DetailField label="City / Slot">
            {[row.booking_city || meta.city, row.pickup_slot_label || meta.pickup_slot_label]
              .filter(Boolean)
              .join(' · ') || '—'}
          </DetailField>
          <DetailField label="Pickup">{fmtDate(row.booking_date)}</DetailField>
          <DetailField label="Amount paid">₹{Number(paid || 0).toLocaleString('en-IN')}</DetailField>
          <DetailField label="Coupon">{row.coupon_code || meta.coupon_code}</DetailField>
          <DetailField label="Payment ID">{row.razorpay_payment_id || meta.razorpay_payment_id}</DetailField>
          <DetailField label="Status"><StatusBadge value={row.status} /></DetailField>
          <DetailField label="Booked">{fmtDate(row.created_at)}</DetailField>
          <div className="pt-4">
            <EnquiryActionButtons
              closed={isSimpleEnquiryClosed(row)}
              updating={updating[row.id]}
              onComplete={() => handleEnquiryAction('car_service', row, 'complete')}
              onCancel={() => handleEnquiryAction('car_service', row, 'cancel')}
            />
          </div>
        </>
      );
    }
    if (activeTab === 'website') {
      const row = detailRow;
      return (
        <>
          <DetailField label="Message">
            <p className="whitespace-pre-wrap">{row.message || '—'}</p>
          </DetailField>
          <DetailField label="City">{row.city}</DetailField>
          <DetailField label="Service">{row.service}</DetailField>
          <DetailField label="Source">{row.source || 'website'}</DetailField>
          <DetailField label="Status"><StatusBadge value={row.status || 'new'} /></DetailField>
          <DetailField label="Submitted">{fmtDate(row.created_at)}</DetailField>
          <div className="pt-4">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Update status</label>
            <select
              value={row.status || 'new'}
              disabled={!!updating[row.id]}
              onChange={(e) => handleWebsiteStatus(row, e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {WEBSITE_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </>
      );
    }
    return null;
  };

  const searchPlaceholder =
    activeTab === 'website'
      ? 'Search name, phone, message...'
      : 'Search name, phone, vehicle, card no, PAN...';

  const emptyHint =
    activeTab === 'website'
      ? 'Contact form messages from app.roadserve.in will appear here.'
      : activeTab === 'rsa' && rsaStatus === 'completed'
        ? 'No completed RSA jobs yet.'
        : activeTab === 'rsa' && rsaStatus === 'cancelled'
          ? 'No cancelled RSA jobs.'
          : activeTab === 'rsa' && rsaStatus === 'open'
            ? 'No open RSA jobs right now.'
            : activeTab === 'rsa' && rsaStatus === 'scheduled'
              ? 'No upcoming scheduled jobs waiting for a partner.'
            : activeTab === 'rsa' && rsaSource === 'b2b'
              ? 'No B2B jobs for this filter.'
            : activeTab === 'rsa' && rsaSource === 'website'
              ? 'No website complaints for this filter.'
            : 'New app submissions will appear here.';

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">App Enquiries</h1>
          <p className="text-slate-600 mt-1 text-sm">
            Live app, WhatsApp AI, B2B portal & website enquiries — click a name for full details
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium"
        >
          <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          const unreadCount = unread[tab.id] || 0;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setDetailRow(null);
                setActiveTab(tab.id);
              }}
              className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${
                active
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              <Icon size={16} />
              {tab.label}
              {unreadCount > 0 ? (
                <span
                  className={`min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center ${
                    active ? 'bg-white text-primary-700' : 'bg-red-500 text-white'
                  }`}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {activeTab === 'rsa' ? (
        <div className="flex flex-wrap items-center gap-2">
          {RSA_STATUS_FILTERS.map((filter) => {
            const active = rsaStatus === filter.id;
            const Icon = filter.icon;
            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => setRsaStatus(filter.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {Icon ? <Icon size={13} /> : null}
                {filter.label}
              </button>
            );
          })}
          <span className="hidden sm:inline-block w-px h-5 bg-slate-200 mx-1" aria-hidden />
          {RSA_SOURCE_FILTERS.map((filter) => {
            const active = rsaSource === filter.id;
            const Icon = filter.icon;
            return (
              <button
                key={`src-${filter.id}`}
                type="button"
                onClick={() => setRsaSource(filter.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                  active
                    ? filter.id === 'b2b'
                      ? 'bg-indigo-700 text-white border-indigo-700'
                      : filter.id === 'website'
                        ? 'bg-amber-600 text-white border-amber-600'
                        : 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {Icon ? <Icon size={13} /> : null}
                {filter.label}
              </button>
            );
          })}
        </div>
      ) : null}

      <form
        onSubmit={handleSearch}
        className="bg-white rounded-xl p-3 shadow-sm border border-slate-200 flex flex-wrap gap-2"
      >
        <div className="relative flex-1 min-w-[220px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
        >
          Search
        </button>
        {search ? (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setSearchInput('');
            }}
            className="px-4 py-2.5 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
          >
            Clear
          </button>
        ) : null}
      </form>

      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-64 bg-white rounded-xl border border-slate-200">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4" />
              <p className="text-slate-600">Loading enquiries...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="py-12 text-center bg-white rounded-xl border border-red-200">
            <p className="text-lg font-medium text-red-700">Failed to load enquiries</p>
            <p className="text-sm text-red-600 mt-1 px-4">{error?.message || 'Unknown error'}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm"
            >
              <RefreshCw size={14} />
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-slate-500 bg-white rounded-xl border border-slate-200">
            <Car size={28} className="mx-auto mb-3 text-slate-300" />
            <p className="text-lg font-medium text-slate-700">No enquiries found</p>
            <p className="text-sm mt-1">{search ? 'Try a different search term.' : emptyHint}</p>
          </div>
        ) : (
          <>
            {activeTab === 'car_loan' && renderCarLoanCards()}
            {activeTab === 'car_service' && renderCarServiceCards()}
            {activeTab === 'rsa' && renderRsaCards()}
            {activeTab === 'website' && renderWebsiteCards()}

            <div ref={loadMoreRef} className="py-4 flex items-center justify-center">
              {isFetchingNextPage ? (
                <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                  <RefreshCw size={14} className="animate-spin" />
                  Loading more...
                </div>
              ) : hasNextPage ? (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50"
                >
                  Load more
                </button>
              ) : (
                <span className="text-xs text-slate-400">End of list · {rows.length} shown</span>
              )}
            </div>
          </>
        )}
      </div>

      {detailRow && activeTab !== 'rsa' ? (
        <EnquiryDetailDrawer tab={activeTab} row={detailRow} onClose={() => setDetailRow(null)}>
          {renderDetailBody()}
        </EnquiryDetailDrawer>
      ) : null}
    </div>
  );
};

export default AppEnquiries;
